/**
 * Auth Store
 *
 * express-session + redis 기반 인증 상태 관리
 * - localStorage 대신 세션 쿠키 사용
 * - 토큰 없음, 세션만 사용
 */

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { authApi } from '../api/authApi';
import type { User, LoginCredentials } from '../types/auth.types';
import {
    validateEmail,
    validateEmailWhileTyping,
    suggestEmailCorrection,
    type EmailValidationResult,
    type EmailValidationOptions
} from '../utils/validation';
import { getCachedBrowserFingerprint } from '../utils/browserFingerprint';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';
import { resolveDefaultLandingPathFromAuth } from '@/shared/utils/permissionUtils';
import { isAdminOnlySession } from '@/shared/utils/authSessionUtils';

export const useAuthStore = defineStore('auth', () => {
    // State
    const user = ref<User | null>(null);
    const isInitialized = ref(false);
    
    // Composable에서 가져온 상태들
    const isLoading = ref(false);
    const error = ref<string | null>(null);

    // Email validation states
    const emailValidationError = ref<string | null>(null);
    const emailSuggestion = ref<string | null>(null);

    // Login verification state
    const loginToken = ref<string | null>(null);

    // Getters
    const isAuthenticated = computed(() => !!user.value);
    const currentUser = computed(() => user.value);

    // Actions
    const login = async (credentials: LoginCredentials) => {
        isLoading.value = true;
        error.value = null;

        try {
            // 브라우저 지문 생성 (비동기) - 항상 해시 값을 반환
            let browserFingerprint = await getCachedBrowserFingerprint();
            
            if (import.meta.env.DEV) console.log('[AuthStore] Generated browserFingerprint:', browserFingerprint?.substring(0, 30) + '...', 'Length:', browserFingerprint?.length);
            
            // 최종 검증 - UNKNOWN이나 유효하지 않은 값이면 에러
            if (!browserFingerprint || 
                browserFingerprint === 'UNKNOWN' || 
                browserFingerprint.trim() === '' ||
                browserFingerprint.length < 10) {
                if (import.meta.env.DEV) console.error('[AuthStore] 유효하지 않은 browserFingerprint:', browserFingerprint);
                throw new Error('Failed to generate browser fingerprint');
            }
            
            // 입력 정리
            const payload = {
                email: (credentials as any).email?.trim?.() || (credentials as any).username?.trim?.() || '',
                password: String(credentials.password ?? '').trim(),
                browserFingerprint: browserFingerprint // 항상 해시 값이어야 함
            } as any;
            
            if (import.meta.env.DEV) console.log('[AuthStore] Login payload:', {
                email: payload.email.substring(0, 5) + '***',
                hasPassword: !!payload.password,
                hasBrowserFingerprint: !!payload.browserFingerprint,
                browserFingerprintLength: payload.browserFingerprint?.length
            });
            
            const response = await authApi.login(payload);

            // MIG 플로우: 비밀번호 변경 링크가 이메일로 발송된 경우
            // 응답 형식: { success: true, migPasswordResetSent: true, message: ... }
            if ((response as any).success && (response as any).migPasswordResetSent) {
                return {
                    success: true,
                    migPasswordResetSent: true,
                    message: (response as any).message || 'Password reset link has been sent to your email.'
                };
            }

            // 인증이 필요한 경우 (이메일/MFA)
            if ((response as any).authRequired && (response as any).loginToken) {
                loginToken.value = (response as any).loginToken;
                return {
                    success: false,
                    authRequired: true,
                    authType: (response as any).authType,
                    loginToken: (response as any).loginToken,
                    message: (response as any).message
                };
            }

            if (response.success) {
                // 백엔드 응답 형태 호환: { success, user } 또는 { success, data: { user } }
                const u = (response as any).user || (response as any).data?.user || null;
                user.value = u;
                loginToken.value = null; // 정상 로그인 시 토큰 제거

                // BIGO='MIG'인 경우 비밀번호 변경 페이지로 리다이렉트 (이미 처리됨)
                if ((response as any).requirePasswordChange && (response as any).userEmail) {
                    const { useRouter } = await import('vue-router');
                    const router = useRouter();
                    router.push(`/auth/forgot-password2?email=${encodeURIComponent((response as any).userEmail)}&mig=true`);
                    return { 
                        success: true, 
                        user: u, 
                        requirePasswordChange: true,
                        userEmail: (response as any).userEmail
                    };
                }

                // sessionStore 초기화 플래그 리셋하여 다음 initializeSession에서 새 정보 로드
                const { useSessionStore } = await import('@/shared/stores/sessionStore');
                const sessionStore = useSessionStore();
                sessionStore.isInitialized = false;

                // 로그인 성공 시 사용자의 기본 언어로 설정
                const userLanguage = u?.user?.language || u?.language;
                if (userLanguage) {
                    const languageStore = useLanguageStore();
                    // 백엔드는 대문자(EN, KO 등), 프론트엔드는 소문자(en, ko 등) 사용
                    const normalizedLang = userLanguage.toLowerCase();
                    languageStore.setLanguage(normalizedLang);
                    if (import.meta.env.DEV) console.log('[AuthStore] 로그인 시 사용자 기본 언어 적용:', normalizedLang);
                }

                // 세션 쿠키는 서버에서 자동으로 설정됨 (connect.sid)
                return { success: true, user: u };
            } else {
                const msg = (response as any).message || (response as any).error?.message || 'Login failed';
                error.value = msg;
                return { success: false, error: msg };
            }
        } catch (err: any) {
            const data = err?.response?.data || {};
            const msg = data?.message || data?.error?.message || err?.message || 'Login failed';
            error.value = msg;
            return { success: false, error: msg };
        } finally {
            isLoading.value = false;
        }
    };

    // Remember Me 지원 로그인
    const loginWithRemember = async (credentials: LoginCredentials, remember: boolean) => {
        const result = await login(credentials);
        if (result.success) {
            if (remember) {
                localStorage.setItem('remembered-email', (credentials as any).email || (credentials as any).username || '');
            } else {
                localStorage.removeItem('remembered-email');
            }
        }
        return result;
    };

    const logout = async () => {
        isLoading.value = true;
        error.value = null;

        try {
            await authApi.logout();
            user.value = null;
            isInitialized.value = false;

            // Pinia store.$dispose()는 동일 인스턴스를 참조하는 컴포넌트/싱글톤과 꼬여
            // 재로그인 후 버튼·권한이 먹통이 되는 원인이 됨 → 세션만 명시적으로 비우고 인스턴스는 유지
            const { useSessionStore } = await import('@/shared/stores/sessionStore');
            useSessionStore().clearSession();

            // 세션 쿠키는 서버에서 자동으로 삭제됨
            return { success: true };
        } catch (err: any) {
            error.value = err.message || 'Logout failed';
            return { success: false, error: err.message };
        } finally {
            isLoading.value = false;
        }
    };

    const fetchCurrentUser = async () => {
        try {
            const response = await authApi.getCurrentUser();
            if (response.success && response.data) {
                if (isAdminOnlySession(response.data.user || response.data)) {
                    user.value = null;
                    isInitialized.value = true;
                    return false;
                }
                user.value = response.data.user;
                isInitialized.value = true;
                return true;
            } else {
                user.value = null;
                isInitialized.value = true;
                return false;
            }
        } catch (error) {
            console.error('Failed to fetch current user:', error);
            user.value = null;
            isInitialized.value = true;
            return false;
        }
    };

    const checkSession = async () => {
        try {
            const response = await authApi.checkSession();
            // apiClient.get은 response.data를 반환하므로 이미 래핑 해제된 상태
            // 응답이 객체인 경우 authenticated 필드를 확인
            if (response && typeof response === 'object') {
                const data = response as any;
                // authenticated 필드가 명시적으로 true이고 user가 있는 경우만 인증됨으로 판단
                const hasUser = !!(data.user || data.hasUser);
                // 어드민 세션은 사용자 FE에서 로그인으로 취급하지 않음 (쿠키 공유 시 리다이렉트 루프 방지)
                if (hasUser && isAdminOnlySession(data.user || data)) {
                    if (import.meta.env.DEV) console.log('checkSession: admin-only session ignored for user app');
                    return false;
                }
                const isAuthenticated = data.authenticated === true && hasUser;
                if (import.meta.env.DEV) console.log('checkSession result:', {
                    authenticated: data.authenticated,
                    hasUser,
                    isAuthenticated
                });
                // 반드시 boolean 반환 (객체가 아닌 true/false)
                return isAuthenticated === true;
            }
            // 응답이 boolean인 경우 그대로 반환
            return response === true;
        } catch (error) {
            if (import.meta.env.DEV) console.log('checkSession error:', error);
            return false;
        }
    };

    /**
     * 세션 정보 조회
     * sessionStore에서 사용하기 위한 메서드
     */
    const getSessionInfo = async () => {
        try {
            const response = await authApi.getSessionInfo();
            return response;
        } catch (error) {
            console.error('Get session info error:', error);
            throw error;
        }
    };

    // ===== Router Integration Methods =====
    /**
     * 로그인 후 적절한 페이지로 이동
     * - isAuthEnabled=false: jurisdiction 기준 Entity List
     * - isAuthEnabled=true: Dashboard → Entity → /entity-no-permission
     */
    const loginWithRedirect = async (credentials: LoginCredentials, router: any) => {
        const result = await login(credentials);

        if (result.success) {
            const route = router.currentRoute.value;
            const redirectParam = route.query.redirect as string | undefined;
            
            // Open Redirect 방지: 내부 경로만 허용
            const isSafeRedirect = (url: string): boolean => {
                return url.startsWith('/') && !url.startsWith('//') && !url.includes('://');
            };

            if (redirectParam && isSafeRedirect(redirectParam)) {
                // 딥링크 경로(/d/로 시작)인 경우 전체 페이지 리로드로 처리
                if (redirectParam.startsWith('/d/')) {
                    window.location.href = redirectParam;
                    return true;
                }
                await router.push(redirectParam);
            } else {
                if (redirectParam) {
                    console.warn('[Auth] Blocked unsafe redirect:', redirectParam);
                }
                const landingPath = resolveDefaultLandingPathFromAuth(
                    user.value,
                    user.value?.customer?.jurisdiction,
                );
                await router.push(landingPath);
            }
            return true;
        }
        return false;
    };

    /**
     * 로그아웃 후 로그인 페이지로 이동
     */
    const logoutWithRedirect = async (router: any) => {
        const result = await logout();
        
        if (result.success) {
            await router.push('/auth/login');
            return true;
        }
        return false;
    };

    // 모든 데이터 정리 (디버깅용)
    const clearAllData = () => {
        if (import.meta.env.DEV) console.log('Clearing all auth data...');
        user.value = null;
        isInitialized.value = false;
        isLoading.value = false;
        error.value = null;
        
        // localStorage 정리 (혹시 남아있는 데이터가 있다면)
        localStorage.clear();
        sessionStorage.clear();
        
        // 쿠키 정리 (document.cookie에서 직접 삭제)
        document.cookie.split(";").forEach((c) => {
            const eqPos = c.indexOf("=");
            const name = eqPos > -1 ? c.substr(0, eqPos) : c;
            document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
        });
        
        if (import.meta.env.DEV) console.log('All data cleared');
    };

    /**
     * 고객 정보 가져오기
     */
    const getCustomerInfo = () => user.value?.customer || null;

    /**
     * 사용자 정보 가져오기
     */
    const getUserInfo = () => user.value?.user || null;

    /**
     * 세션 타입 가져오기
     */
    const getSessionType = () => user.value?.sessionType || null;

    // 앱 시작 시 세션 확인
    const initialize = async () => {
        await fetchCurrentUser();
    };

    // ===== Error Management =====
    const clearError = () => {
        error.value = null;
    };

    const setError = (message: string) => {
        error.value = message;
    };

    // ===== Email Validation Methods =====
    /**
     * 이메일 유효성 검사 (폼 제출 시)
     * @param email 검사할 이메일
     * @param options 검증 옵션
     * @returns 검증 결과
     */
    const validateEmailInput = (
        email: string,
        options?: EmailValidationOptions
    ): EmailValidationResult => {
        // 번역 함수 추가
        const languageStore = useLanguageStore();
        const { createSyncT } = useTranslation();
        const t = createSyncT(languageStore.currentLanguage);
        
        const result = validateEmail(email, { ...options, t });
        emailValidationError.value = result.isValid ? null : result.message || null;

        // 이메일 수정 제안
        if (!result.isValid && email) {
            const suggestion = suggestEmailCorrection(email);
            emailSuggestion.value = suggestion;
        } else {
            emailSuggestion.value = null;
        }

        return result;
    };

    /**
     * 실시간 이메일 유효성 검사 (타이핑 중)
     * @param email 검사할 이메일
     * @returns 오류 메시지 또는 null
     */
    const validateEmailRealtime = (email: string): string | null => {
        // 번역 함수 추가
        const languageStore = useLanguageStore();
        const { createSyncT } = useTranslation();
        const t = createSyncT(languageStore.currentLanguage);
        
        const error = validateEmailWhileTyping(email, t);
        emailValidationError.value = error;

        // 타이핑 중에는 제안을 표시하지 않음
        emailSuggestion.value = null;

        return error;
    };

    /**
     * 이메일 검증 상태 초기화
     */
    const clearEmailValidation = () => {
        emailValidationError.value = null;
        emailSuggestion.value = null;
    };

    /**
     * 이메일 제안 적용
     * @param suggestion 적용할 이메일 제안
     */
    const applyEmailSuggestion = (suggestion: string) => {
        emailSuggestion.value = null;
        emailValidationError.value = null;
        return suggestion;
    };

    /**
     * 로그인 전 이메일 검증
     * @param email 이메일 주소
     * @returns 유효한 경우 true
     */
    const canProceedWithEmail = (email: string): boolean => {
        const result = validateEmailInput(email, {
            strict: false,
            allowEmpty: false
        });
        return result.isValid;
    };

    /**
     * 로그인 이메일 인증 코드 검증
     * @param token 로그인 토큰
     * @param emailCode 이메일 인증 코드 (6자리)
     * @returns 검증 결과
     */
    const verifyEmailToken = async (token: string, emailCode: string) => {
        isLoading.value = true;
        error.value = null;

        try {
            const response = await authApi.verifyLoginEmailCode(token, emailCode);

            if (response.success) {
                const u = (response as any).user || (response as any).data?.user || null;
                user.value = u;
                loginToken.value = null; // 검증 완료 후 토큰 제거
                return { success: true, user: u };
            } else {
                const msg = (response as any).message || (response as any).error?.message || 'Verification failed';
                error.value = msg;
                return { success: false, error: msg };
            }
        } catch (err: any) {
            const data = err?.response?.data || {};
            const msg = data?.message || data?.error?.message || err?.message || 'Verification failed';
            error.value = msg;
            throw err; // 호출부에서 에러 처리할 수 있도록 throw
        } finally {
            isLoading.value = false;
        }
    };

    /**
     * 로그인 MFA 코드 검증
     * @param token 로그인 토큰
     * @param mfaCode MFA 코드 (6자리)
     * @returns 검증 결과
     */
    const verifyMFACode = async (token: string, mfaCode: string) => {
        isLoading.value = true;
        error.value = null;

        try {
            const response = await authApi.verifyLoginMFACode(token, mfaCode);

            if (response.success) {
                const u = (response as any).user || (response as any).data?.user || null;
                user.value = u;
                loginToken.value = null; // 검증 완료 후 토큰 제거
                return { success: true, user: u };
            } else {
                const msg = (response as any).message || (response as any).error?.message || 'Verification failed';
                error.value = msg;
                return { success: false, error: msg };
            }
        } catch (err: any) {
            const data = err?.response?.data || {};
            const msg = data?.message || data?.error?.message || err?.message || 'Verification failed';
            error.value = msg;
            throw err; // 호출부에서 에러 처리할 수 있도록 throw
        } finally {
            isLoading.value = false;
        }
    };

    /**
     * 이메일 재발송 (로그인 플로우용)
     * 재발송 전용 엔드포인트 사용 (FDS 재검증 없음)
     * @param email 이메일 주소
     * @param loginToken 로그인 토큰 (선택적, 없으면 store에서 가져옴)
     * @returns 재발송 결과
     */
    const resendVerificationEmail = async (email: string, loginTokenParam?: string) => {
        isLoading.value = true;
        error.value = null;

        try {
            const token = loginTokenParam || loginToken.value;
            
            if (!token) {
                throw new Error('Login token is required for resending verification email');
            }

            const response = await authApi.resendLoginEmailCode(token, email);
            const responseData = (response as any).data || response;

            if (responseData.success) {
                return { 
                    success: true, 
                    message: responseData.message || 'Verification email has been resent',
                    retryCount: responseData.retryCount
                };
            } else {
                const msg = responseData.message || responseData.error?.message || 'Failed to resend email';
                error.value = msg;
                return { success: false, error: msg };
            }
        } catch (err: any) {
            const data = err?.response?.data || {};
            const msg = data?.message || data?.error?.message || err?.message || 'Failed to resend email';
            error.value = msg;
            throw err; // 호출부에서 에러 처리할 수 있도록 throw
        } finally {
            isLoading.value = false;
        }
    };

    /**
     * loginToken 설정 (로그인 응답에서 받은 토큰 저장)
     */
    const setLoginToken = (token: string | null) => {
        loginToken.value = token;
    };

    /**
     * 구글 로그인
     * @param credential Google JWT credential
     */
    const loginWithGoogle = async (credential: string) => {
        isLoading.value = true;
        error.value = null;

        try {
            const response = await authApi.loginWithGoogle(credential);
            const responseData = (response as any).data || response;

            if (responseData.success || responseData.user) {
                const u = responseData.user || responseData.data?.user || null;
                user.value = u;
                loginToken.value = null;

                // 구글 로그인 성공 시 사용자의 기본 언어로 설정
                const userLanguage = u?.user?.language || u?.language;
                if (userLanguage) {
                    const languageStore = useLanguageStore();
                    // 백엔드는 대문자(EN, KO 등), 프론트엔드는 소문자(en, ko 등) 사용
                    const normalizedLang = userLanguage.toLowerCase();
                    languageStore.setLanguage(normalizedLang);
                    if (import.meta.env.DEV) console.log('[AuthStore] 구글 로그인 시 사용자 기본 언어 적용:', normalizedLang);
                }

                return { success: true, user: u };
            } else {
                const msg = responseData.message || responseData.error?.message || 'Google login failed';
                error.value = msg;
                return { success: false, error: msg, googleSignUp: responseData.googleSignUp, googleData: responseData.googleData };
            }
        } catch (err: any) {
            const data = err?.response?.data || {};
            const msg = data?.message || data?.error?.message || err?.message || 'Google login failed';
            error.value = msg;
            return { 
                success: false, 
                error: msg,
                googleSignUp: data?.googleSignUp,
                googleData: data?.googleData
            };
        } finally {
            isLoading.value = false;
        }
    };

    return {
        // ===== State =====
        user,
        isInitialized,
        isLoading,
        error,
        emailValidationError,
        emailSuggestion,
        loginToken,

        // ===== Getters =====
        isAuthenticated,
        currentUser,

        // ===== Basic Actions =====
        login,
        loginWithRemember,
        logout,

        // ===== Router Integration =====
        loginWithRedirect,
        logoutWithRedirect,

        // ===== Session Management =====
        fetchCurrentUser,
        checkSession,
        getSessionInfo,
        initialize,

        // ===== Utility Methods =====
        getCustomerInfo,
        getUserInfo,
        getSessionType,
        clearAllData,

        // ===== Error Management =====
        clearError,
        setError,

        // ===== Email Validation =====
        validateEmailInput,
        validateEmailRealtime,
        clearEmailValidation,
        applyEmailSuggestion,
        canProceedWithEmail,

        // ===== Login Email Verification =====
        verifyEmailToken,
        verifyMFACode,
        resendVerificationEmail,
        setLoginToken,

        // ===== Google Login =====
        loginWithGoogle,
    };
});
